import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar as ShadCalendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";
import { buildGuestWhatsAppUrl } from "@/lib/whatsapp";
import { toast } from "sonner";
import { Check, X, Clock, MessageCircle, Printer, Users, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]",
  confirmed: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  declined: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
  cancelled: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
};

// ISO date helper (yyyy-mm-dd) using local timezone
const isoLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function StaffCalendar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Date());
  const [month, setMonth] = useState(new Date());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/reservations");
      setItems(data);
    } catch { toast.error("Could not load reservations."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Group active (non-cancelled/declined) reservations by date
  const byDate = useMemo(() => {
    const map = {};
    for (const r of items) {
      if (!map[r.date]) map[r.date] = [];
      map[r.date].push(r);
    }
    // Sort each day by time
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    }
    return map;
  }, [items]);

  const selectedKey = isoLocal(selected);
  const dayList = byDate[selectedKey] || [];
  const activeCount = dayList.filter((r) => r.status !== "cancelled" && r.status !== "declined").length;
  const totalPax = dayList
    .filter((r) => r.status === "confirmed")
    .reduce((sum, r) => sum + (r.party_size || 0), 0);

  // Highlight days that have at least one non-declined reservation
  const modifiers = useMemo(() => {
    const hasAny = [];
    const hasConfirmed = [];
    const hasPending = [];
    for (const [dateStr, rs] of Object.entries(byDate)) {
      const [y, m, d] = dateStr.split("-").map(Number);
      if (!y) continue;
      const dt = new Date(y, m - 1, d);
      const alive = rs.filter((r) => r.status !== "cancelled" && r.status !== "declined");
      if (alive.length === 0) continue;
      hasAny.push(dt);
      if (alive.some((r) => r.status === "confirmed")) hasConfirmed.push(dt);
      else if (alive.some((r) => r.status === "pending")) hasPending.push(dt);
    }
    return { hasAny, hasConfirmed, hasPending };
  }, [byDate]);

  const modifiersClassNames = {
    hasAny: "tbb-day-any",
    hasConfirmed: "tbb-day-confirmed",
    hasPending: "tbb-day-pending",
    selected: "tbb-day-selected",
    today: "tbb-day-today",
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/staff/reservations/${id}/status`, { status });
      setItems((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  // Month summary
  const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
  const monthStats = useMemo(() => {
    let bookings = 0, pax = 0, days = 0;
    for (const [d, rs] of Object.entries(byDate)) {
      if (!d.startsWith(monthKey)) continue;
      const alive = rs.filter((r) => r.status === "confirmed");
      if (alive.length > 0) days += 1;
      bookings += alive.length;
      pax += alive.reduce((s, r) => s + (r.party_size || 0), 0);
    }
    return { bookings, pax, days };
  }, [byDate, monthKey]);

  return (
    <div data-testid="staff-calendar">
      <div className="eyebrow">Calendar</div>
      <h1 className="mt-2 text-4xl">Reservation calendar</h1>
      <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
        A month at a glance. Days with confirmed bookings show a green dot, pending ones show brass.
        Click any day to see the diary, confirm or decline in-place, print, or WhatsApp the guest.
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8 items-start">
        {/* Calendar panel */}
        <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-5">
          <ShadCalendar
            data-testid="calendar-widget"
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            month={month}
            onMonthChange={setMonth}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            className="w-full"
          />

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-[hsl(var(--rule))] flex flex-wrap gap-3 text-[10px] uppercase tracking-widest text-[hsl(var(--ink-soft))]">
            <span className="inline-flex items-center gap-1.5"><span className="dot dot-confirmed" /> Confirmed</span>
            <span className="inline-flex items-center gap-1.5"><span className="dot dot-pending" /> Pending</span>
          </div>

          {/* Month summary */}
          <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-testid="month-summary">
            <StatBox label="Booked days" value={monthStats.days} />
            <StatBox label="Confirmed" value={monthStats.bookings} />
            <StatBox label="Total pax" value={monthStats.pax} />
          </div>
        </div>

        {/* Day panel */}
        <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]" data-testid="day-panel">
          <header className="p-5 border-b border-[hsl(var(--rule))] flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow">The diary</div>
              <div className="mt-1 text-2xl display" data-testid="day-heading">
                {selected.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
              <div className="mt-2 text-xs text-[hsl(var(--ink-soft))]">
                {activeCount} booking{activeCount === 1 ? "" : "s"} · {totalPax} pax (confirmed)
              </div>
            </div>
            <div className="flex gap-1">
              <button data-testid="prev-day" onClick={() => setSelected(new Date(selected.getTime() - 24 * 60 * 60 * 1000))} className="p-2 rounded border border-[hsl(var(--rule))]" title="Previous day">
                <ChevronLeft size={14} />
              </button>
              <button data-testid="today" onClick={() => { const t = new Date(); setSelected(t); setMonth(t); }} className="px-3 py-2 text-xs uppercase tracking-wider border border-[hsl(var(--rule))] rounded">Today</button>
              <button data-testid="next-day" onClick={() => setSelected(new Date(selected.getTime() + 24 * 60 * 60 * 1000))} className="p-2 rounded border border-[hsl(var(--rule))]" title="Next day">
                <ChevronRight size={14} />
              </button>
            </div>
          </header>

          {loading && <div className="p-8 text-[hsl(var(--ink-soft))]">Loading...</div>}

          {!loading && dayList.length === 0 && (
            <div data-testid="day-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
              <Users size={22} className="mx-auto text-[hsl(var(--brass))]" />
              <div className="mt-3">No bookings for this day.</div>
            </div>
          )}

          {!loading && dayList.length > 0 && (
            <ul className="divide-y divide-[hsl(var(--rule))]">
              {dayList.map((r) => (
                <li key={r.id} data-testid={`day-res-${r.id}`} className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="text-lg font-mono">{r.time}</div>
                      <div className="text-lg font-semibold">{r.name}</div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[r.status] || ""}`}>{r.status}</span>
                      <div className="text-sm text-[hsl(var(--ink-soft))]">{r.party_size} pax</div>
                    </div>
                    <div className="mt-1 text-sm text-[hsl(var(--ink-soft))]">
                      {r.phone} · {r.email}
                      {r.occasion && <> · <span className="text-[hsl(var(--ink))]">{r.occasion}</span></>}
                    </div>
                    {r.notes && (
                      <div className="mt-1 text-sm italic text-[hsl(var(--ink-soft))]">"{r.notes}"</div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button data-testid={`cal-confirm-${r.id}`} disabled={r.status === "confirmed"} onClick={() => updateStatus(r.id, "confirmed")} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))] disabled:opacity-40">
                      <Check size={12} /> Confirm
                    </button>
                    <a data-testid={`cal-whatsapp-${r.id}`} href={buildGuestWhatsAppUrl(r, window.location.origin) || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))]">
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                    <Link to={`/staff/reservations/${r.id}/print`} data-testid={`cal-print-${r.id}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))]">
                      <Printer size={12} /> Print
                    </Link>
                    <button data-testid={`cal-pending-${r.id}`} disabled={r.status === "pending"} onClick={() => updateStatus(r.id, "pending")} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))] disabled:opacity-40">
                      <Clock size={12} /> Pending
                    </button>
                    <button data-testid={`cal-decline-${r.id}`} disabled={r.status === "declined"} onClick={() => updateStatus(r.id, "declined")} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] disabled:opacity-40">
                      <X size={12} /> Decline
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <style>{`
        /* Day markers */
        .tbb-day-any { position: relative; }
        .tbb-day-any::after {
          content: "";
          position: absolute;
          bottom: 3px;
          left: 50%;
          transform: translateX(-50%);
          width: 5px; height: 5px;
          border-radius: 999px;
          background: hsl(var(--ink-soft));
        }
        .tbb-day-confirmed::after { background: hsl(var(--forest)); box-shadow: 0 0 0 2px hsl(var(--forest) / 0.15); }
        .tbb-day-pending::after { background: hsl(var(--brass)); box-shadow: 0 0 0 2px hsl(var(--brass) / 0.2); }
        .tbb-day-today {
          font-weight: 700;
          color: hsl(var(--forest));
        }
        .tbb-day-selected { font-weight: 700; }
        .dot { display: inline-block; width: 8px; height: 8px; border-radius: 999px; }
        .dot-confirmed { background: hsl(var(--forest)); }
        .dot-pending { background: hsl(var(--brass)); }
      `}</style>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] py-3">
      <div className="display text-2xl text-[hsl(var(--forest))]">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-[hsl(var(--ink-soft))] mt-0.5">{label}</div>
    </div>
  );
}
